import type { UseQueryOptions } from '@tanstack/react-query';

import { scheduledAppsGetCompressorScheduleForecast } from '@repo/sdk/funcs/scheduledAppsGetCompressorScheduleForecast';
import { CompressorScheduleForecast } from '@repo/sdk/models/components';
import { SDKError } from '@repo/sdk/models/errors';
import { GetCompressorScheduleForecastRequest } from '@repo/sdk/models/operations';

import { GaiaClientFunction } from '@/client/types';
import {
  addUTCMilliseconds,
  createUTCDate,
  subtractUTCMilliseconds,
} from '@/helpers/datetime/utc-date';
import { GaiaQueryWithAgentId } from '@/queries/types';

const ENERGY_AI_LOOKBACK_MINUTES = 60;
const ENERGY_AI_FORECAST_MINUTES = 60;

interface GetCompressorScheduleForecastQueryProps extends GaiaQueryWithAgentId {
  client: GaiaClientFunction;
  enabled?: boolean;
}

export const getCompressorScheduleForecastQuery = ({
  agentId,
  client,
  enabled = true,
}: GetCompressorScheduleForecastQueryProps): UseQueryOptions<
  CompressorScheduleForecast,
  SDKError,
  CompressorScheduleForecast | undefined
> => {
  return {
    enabled: enabled && !!agentId,
    queryKey: [agentId, 'compressorScheduleForecast'],
    queryFn: () => {
      const now = createUTCDate();
      const since = subtractUTCMilliseconds(ENERGY_AI_LOOKBACK_MINUTES * 60 * 1000, now);
      const until = addUTCMilliseconds(ENERGY_AI_FORECAST_MINUTES * 60 * 1000, now);

      return client<GetCompressorScheduleForecastRequest, CompressorScheduleForecast>({
        gaiaFunc: scheduledAppsGetCompressorScheduleForecast,
        request: {
          agentId: agentId!,
          since,
          until,
        },
      });
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  };
};
